/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { EntryRequest } from 'modules/teams/domain/interfaces/entry-request';
import { updateEntryRequest } from 'modules/teams/store/entry-requests/actions';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { EntryRequestStatus } from 'shared/types/entry-request-status';
import { getBirthDay, getFullName } from 'shared/utils';

import { AppDispatch } from 'store/store';
import { theme } from 'styles/theme/theme';
import { UDButton, UDFilter, UDList, UDListItem, UDText } from 'ud-ui/components';
import { ParticipantsList } from '../participants/styles';

interface WaitingListProps {
  entryRequests: EntryRequest[];
}

const WaitingList = ({ entryRequests }: WaitingListProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState('');

  const changeEntryRequestStatus = useCallback(
    (entryRequest: EntryRequest, status: EntryRequestStatus) => {
      try {
        dispatch(updateEntryRequest({ entryRequestId: entryRequest.requestId, status }))
          .unwrap()
          .then(() => {
            toast.success(
              t('entryRequest.changeStatus', {
                entryRequest: getFullName(entryRequest),
                status: t(`entryRequest.status.${status}`),
              }),
            );
          })
          .catch(() => {
            toast.error(
              t('entryRequest.changeStatusError', {
                entryRequest: getFullName(entryRequest),
              }),
            );
          });
      } catch (error) {
        toast.error(
          t('entryRequest.changeStatusError', {
            entryRequest: getFullName(entryRequest),
          }),
        );
      }
    },
    [t, dispatch],
  );

  const entryRequestsByFilter = useMemo(() => {
    return entryRequests.filter((entryRequest) => {
      return entryRequest.name.toLowerCase().includes(filterValue.toLowerCase());
    });
  }, [entryRequests, filterValue]);

  return (
    <ParticipantsList>
      <UDFilter
        inputPlaceholder={t('participant.filter.inputPlaceholder')}
        value={filterValue}
        onChange={setFilterValue}
        style={{ marginBottom: 10 }}
      />
      <UDList style={{ overflow: 'unset', minHeight: 'calc(100vh - 314px)' }}>
        {entryRequestsByFilter.length > 0 ? (
          entryRequestsByFilter.map((entryRequest, index) => (
            <UDListItem
              key={entryRequest.id}
              index={index + 1}
              image={entryRequest.photo?.externalUrl ?? USER_DEFAULT_AVATAR}
              title={getFullName(entryRequest)}
              subtitle={getBirthDay({
                birthDate: new Date(entryRequest?.birthDate ?? ''),
              })}
              additionalFields={[
                <div key={entryRequest.id} className="flex-center" style={{ marginLeft: 'auto', paddingLeft: 10 }}>
                  <UDButton
                    style={{
                      width: 144,
                      marginRight: 30,
                      outlineColor: theme.colors.Green,
                    }}
                    variant="secondary"
                    onClick={() => changeEntryRequestStatus(entryRequest, 'ACCEPTED')}
                  >
                    {t('entryRequest.status.ACCEPTED')}
                  </UDButton>

                  <UDButton
                    style={{ width: 144, outlineColor: theme.colors.Pink }}
                    variant="secondary"
                    onClick={() => changeEntryRequestStatus(entryRequest, 'DECLINED')}
                  >
                    {t('entryRequest.status.DECLINED')}
                  </UDButton>
                </div>,
              ]}
            />
          ))
        ) : (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100%;
              min-height: calc(100vh - 314px);
            `}
          >
            <UDText color="#8F8E99" type="subhead" className="bold" style={{ fontWeight: 400, fontSize: 16 }}>
              {t('entryRequest.waitingList.empty')}
            </UDText>
          </div>
        )}
      </UDList>
    </ParticipantsList>
  );
};

export default WaitingList;
